import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React from 'react';
import { StyleSheet, View } from 'react-native';

import { Button, Text } from '@/components/ui';
import { useDropboxConnect } from '@/hooks/use-dropbox-connect';
import { usePermissions } from '@/hooks/use-permissions';

/**
 * What the document surfaces show when no storage provider is connected.
 *
 * Replaces the old one-line "not configured in this environment" notice, which
 * was a dead end: it named a retired service and offered no way forward. An admin
 * can now fix the problem from the screen where they hit it, and a non-admin is
 * told who can.
 *
 * `compact` renders the inline variant for panels that sit inside a larger page
 * (an account's Documents tab, portfolio search results); the default is the
 * full-screen version for the Documents browser itself.
 */
export const StorageNotConnected: React.FC<{ compact?: boolean; what?: string }> = ({
	compact = false,
	what = 'Documents',
}) => {
	// Connecting storage is an /integrations write: `hasDocumentOperatorCapability` on the server.
	const { isDocumentOperator: canConnect } = usePermissions();
	const { connect, connecting } = useDropboxConnect();

	if (compact) {
		return (
			<View style={styles.compact} testID="storage-not-connected">
				<Text style={styles.compactText}>
					Dropbox isn&apos;t connected, so {what.toLowerCase()} can&apos;t be shown.
				</Text>
				{canConnect ? (
					<Button
						mode="text"
						compact
						testID="btn-connect-dropbox-inline"
						disabled={connecting}
						onPress={() => router.push('/settings/integrations')}
					>
						Set up
					</Button>
				) : null}
			</View>
		);
	}

	return (
		<View style={styles.full} testID="storage-not-connected">
			<Ionicons name="cloud-offline-outline" size={56} color="#b0bec5" />
			<Text style={styles.title}>Dropbox isn&apos;t connected</Text>
			<Text style={styles.body}>
				{what} are stored in Dropbox. Once an administrator connects an account, association and owner
				folders appear here.
			</Text>

			{canConnect ? (
				<>
					<Button
						mode="contained"
						testID="btn-connect-dropbox"
						style={styles.cta}
						loading={connecting}
						disabled={connecting}
						onPress={connect}
					>
						Connect to Dropbox
					</Button>
					<Button
						mode="text"
						testID="btn-manage-integrations"
						disabled={connecting}
						onPress={() => router.push('/settings/integrations')}
					>
						Manage integrations
					</Button>
				</>
			) : (
				<Text style={styles.hint}>
					Ask an administrator to connect it in Settings → Integrations.
				</Text>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	full: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32, gap: 12 },
	title: { fontSize: 20, fontWeight: 'bold', marginTop: 4 },
	body: { fontSize: 14, color: '#607d8b', textAlign: 'center', maxWidth: 420, lineHeight: 20 },
	cta: { marginTop: 8, minWidth: 220 },
	hint: { fontSize: 13, color: '#90a4ae', textAlign: 'center', marginTop: 4 },
	compact: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 4, padding: 12 },
	compactText: { fontSize: 13, color: '#666' },
});
